import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { CreateTreatmentUpdateMessageParams } from './dtos/createMessage.dto';
import type { CreateTreatmentUpdateTopicParams } from './dtos/createTopic.dto';
import type { GetAllTreatmentUpdatesAvailableParams } from './dtos/getAllAvailable.dto';
import type { GetAllTreatmentUpdatesByCollaboratorParams } from './dtos/getAllByCollaborator.dto';
import type { ImportTreatmentUpdateTopicsParams } from './dtos/importTopics.dto';
import type { RemoveTreatmentUpdateTopicParams } from './dtos/remove.dto';
import type { RemoveAllTreatmentUpdateTopicsParams } from './dtos/removeAll.dto';
import type { ReorderTreatmentUpdateTopicsParams } from './dtos/reorder.dto';
import type { UpdateTreatmentUpdateTopicParams } from './dtos/update.dto';
import type { UpdateTreatmentUpdateMessageParams } from './dtos/updateMessage.dto';
import type { TreatmentUpdatesRepository } from './repository';
import type { CreateMessageBody } from './types/createMessage.type';
import type { CreateTopicBody } from './types/createTopic.type';
import type {
  GetAllTreatmentUpdatesAvailableOutput,
  GetAllTreatmentUpdatesAvailableResponse,
} from './types/getAllAvailable.type';
import type {
  GetAllTreatmentUpdatesByCollaboratorOutput,
  GetAllTreatmentUpdatesByCollaboratorResponse,
} from './types/getAllByCollaborator.type';
import type {
  GetTreatmentUpdateByIdDataResponse,
  GetTreatmentUpdateByIdOutput,
} from './types/getById.type';
import type {
  ImportTopicsOutput,
  ImportTopicsResponse,
} from './types/importTopics.type';
import type { ReorderTopicsBody } from './types/reorderTopics.type';
import type { UpdateMessageBody } from './types/updateMessage.type';
import type { UpdateTopicBody } from './types/updateTopic.type';

class TreatmentUpdatesService implements TreatmentUpdatesRepository {
  public async getAllByCollaborator(
    params?: GetAllTreatmentUpdatesByCollaboratorParams,
  ): Promise<GetAllTreatmentUpdatesByCollaboratorOutput> {
    const { data } = await backendHttpClient.get<
      GetAllTreatmentUpdatesByCollaboratorResponse,
      GetAllTreatmentUpdatesByCollaboratorParams
    >({
      path: 'treatment-updates/collaborator',
      params,
    });

    const output: GetAllTreatmentUpdatesByCollaboratorOutput = {
      list: data.list.map(item => ({
        amount: item.amount,
        startedAt: item.started_at,
        expiredAt: item.expired_at,
        id: item.id,
        priority: item.priority,
        status: item.status,
        title: item.title,
      })),
      pagination: {
        page: params?.page || 1,
        totalPages: data.totalPages,
      },
    };
    return output;
  }

  public async getAllAvailable(
    params?: GetAllTreatmentUpdatesAvailableParams,
  ): Promise<GetAllTreatmentUpdatesAvailableOutput> {
    const { data } = await backendHttpClient.get<
      GetAllTreatmentUpdatesAvailableResponse,
      GetAllTreatmentUpdatesAvailableParams
    >({
      path: 'treatment-updates/available',
      params,
    });

    const output: GetAllTreatmentUpdatesAvailableOutput = {
      list: data.list.map(item => ({
        amount: item.amount,
        createdAt: item.created_at,
        id: item.id,
        priority: item.priority,
        status: item.status,
        title: item.title,
      })),
      pagination: {
        page: params?.page || 1,
        totalPages: data.totalPages,
      },
    };
    return output;
  }

  public async getById(id: string): Promise<GetTreatmentUpdateByIdOutput> {
    const { data: treatment } =
      await backendHttpClient.get<GetTreatmentUpdateByIdDataResponse>({
        path: `treatment-updates/${id}`,
      });
    const output: GetTreatmentUpdateByIdOutput = {
      treatment: {
        amount: treatment.amount,
        collaborator: treatment.collaborator,
        expiredAt: treatment.expired_at,
        id: treatment.id,
        priority: treatment.priority,
        status: treatment.status,
        title: treatment.title,
        topics: treatment.topics,
        messages: treatment.messages.map(item => ({
          createdAt: item.created_at,
          id: item.id,
          message: item.message,
          publishedAt: item.published_at,
          treatmentId: item.treatment_update_id,
          type: item.type,
        })),
      },
    };
    return output;
  }

  public async assignCollaborator(id: string): Promise<void> {
    await backendHttpClient.get<void>({
      path: `treatment-updates/${id}/collaborator`,
    });
  }

  public async unassignCollaborator(id: string): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `treatment-updates/${id}/collaborator`,
    });
  }

  public async removeAllTopics(
    params: RemoveAllTreatmentUpdateTopicsParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `treatment-updates/${params.treatmentUpdateId}/topics`,
    });
  }

  public async removeTopic(
    params: RemoveTreatmentUpdateTopicParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `treatment-updates/${params.treatmentUpdateId}/topics/${params.id}`,
    });
  }

  public async updateTopic(
    params: UpdateTreatmentUpdateTopicParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateTopicBody>({
      path: `treatment-updates/${params.treatmentUpdateId}/topics/${params.id}`,
      body: {
        content: params.content,
        // position: params.position,
        title: params.title,
      },
    });
  }

  public async createMessage(
    params: CreateTreatmentUpdateMessageParams,
  ): Promise<void> {
    await backendHttpClient.post<void, CreateMessageBody>({
      path: `treatment-updates/${params.treatmentUpdateId}/messages`,
      body: {
        message: params.message,
      },
    });
  }

  public async updateMessage(
    params: UpdateTreatmentUpdateMessageParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateMessageBody>({
      path: `treatment-updates/${params.treatmentUpdateId}/messages/${params.treatmentUpdateMessageId}`,
      body: {
        message: params.message,
      },
    });
  }

  public async createTopic(
    params: CreateTreatmentUpdateTopicParams,
  ): Promise<void> {
    await backendHttpClient.post<void, CreateTopicBody>({
      path: `treatment-updates/${params.treatmentUpdateId}/topics`,
      body: {
        content: params.content,
        position: params.position,
        title: params.title,
      },
    });
  }

  public async importTopics(
    params: ImportTreatmentUpdateTopicsParams,
  ): Promise<ImportTopicsOutput> {
    const formData = new FormData();

    formData.append('file', params.file);

    const { data } = await backendHttpClient.post<
      ImportTopicsResponse,
      FormData
    >({
      path: `treatment-updates/${params.treatmentUpdateId}/topics/import`,
      body: formData,
    });

    return {
      topicsCreated: data.topicsCreated,
      warnings: data.warnings,
    };
  }

  public async reorderTopics(
    params: ReorderTreatmentUpdateTopicsParams,
  ): Promise<void> {
    await backendHttpClient.patch<void, ReorderTopicsBody>({
      path: `treatment-updates/${params.treatmentUpdateId}/topics`,
      body: {
        ids: params.ids,
      },
    });
  }

  public async markReview(id: string): Promise<void> {
    await backendHttpClient.get<void>({
      path: `treatment-updates/${id}/review`,
    });
  }
}

export const treatmentUpdatesService = new TreatmentUpdatesService();
